/**
 * Client-side CSV parser for the Finance import wizard (FIN-10).
 *
 * The platform never parses CSV (design §9) — the wizard parses the file in
 * the browser, the user maps columns, and the API receives already-normalized
 * rows. This parser is deliberately small and dependency-free.
 *
 * Behavior:
 *  - Auto-detects the delimiter (`,` vs `;`) by counting occurrences in the
 *    first non-empty line — Portuguese banks frequently export semicolon CSVs.
 *  - Handles quoted fields, including embedded delimiters, newlines, and the
 *    `""` escaped-quote convention (RFC 4180).
 *  - Treats the first non-empty line as the header row.
 *  - Trims surrounding whitespace on unquoted values; preserves quoted values
 *    verbatim (apart from unwrapping the quotes).
 *
 * Number parsing (pt-PT `1.234,56` vs en `1,234.56`) is a separate concern and
 * lives in `parseAmount` below, driven by an explicit format toggle in the UI.
 */

export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

export type NumberFormat = 'pt' | 'en';

/** Detect the most likely field delimiter from a representative line. */
function detectDelimiter(line: string): ',' | ';' {
  // Count delimiters OUTSIDE quoted regions so quoted commas don't skew it.
  let inQuotes = false;
  let comma = 0;
  let semi = 0;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (!inQuotes) {
      if (ch === ',') comma++;
      else if (ch === ';') semi++;
    }
  }
  return semi > comma ? ';' : ',';
}

/**
 * Tokenize the full CSV text into rows of raw string cells, honoring quoted
 * fields that may span multiple physical lines. A single pass over the text.
 */
function tokenize(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  let fieldWasQuoted = false;

  const pushField = () => {
    row.push(fieldWasQuoted ? field : field.trim());
    field = '';
    fieldWasQuoted = false;
  };
  const pushRow = () => {
    pushField();
    rows.push(row);
    row = [];
  };

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];

    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"'; // escaped quote
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }

    if (ch === '"') {
      inQuotes = true;
      fieldWasQuoted = true;
    } else if (ch === delimiter) {
      pushField();
    } else if (ch === '\n') {
      pushRow();
    } else if (ch === '\r') {
      // swallow — a following \n triggers pushRow; a lone \r also ends the row
      if (text[i + 1] !== '\n') pushRow();
    } else {
      field += ch;
    }
  }

  // Trailing field / row (file not ending in a newline).
  if (field.length > 0 || row.length > 0) pushRow();

  return rows;
}

/**
 * Parse CSV text into `{ headers, rows }`. The first non-empty row is the
 * header. Empty trailing rows (blank lines) are dropped.
 */
export function parseCsv(text: string): ParsedCsv {
  const normalized = text.replace(/^\uFEFF/, ''); // strip BOM
  // Find the first non-empty line to detect the delimiter.
  const firstLine =
    normalized.split(/\r?\n/).find((l) => l.trim().length > 0) ?? '';
  if (!firstLine) return { headers: [], rows: [] };

  const delimiter = detectDelimiter(firstLine);
  const all = tokenize(normalized, delimiter).filter(
    (r) => !(r.length === 1 && r[0]!.trim() === ''),
  );
  if (all.length === 0) return { headers: [], rows: [] };

  const [headerRow, ...dataRows] = all;
  return {
    headers: headerRow!.map((h) => h.trim()),
    rows: dataRows,
  };
}

/**
 * Parse a localized money string into a number, or `null` when it can't be
 * read as a number. `format` selects the decimal convention:
 *  - 'pt': `1.234,56`  (dot = thousands, comma = decimal)
 *  - 'en': `1,234.56`  (comma = thousands, dot = decimal)
 * Currency symbols, spaces, and NBSPs are stripped. A trailing/leading `-`
 * and parenthesized negatives `(1.234,56)` are honored.
 */
export function parseAmount(raw: string, format: NumberFormat): number | null {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (s === '') return null;

  // Parenthesized negatives → leading minus.
  let negative = false;
  if (/^\(.*\)$/.test(s)) {
    negative = true;
    s = s.slice(1, -1);
  }

  // Strip everything except digits, separators, and signs.
  s = s.replace(/[^\d.,\-+]/g, '');
  if (s === '' || s === '-' || s === '+') return null;

  if (s.startsWith('-')) {
    negative = !negative;
    s = s.slice(1);
  } else if (s.startsWith('+')) {
    s = s.slice(1);
  }

  if (format === 'pt') {
    // dot = thousands sep, comma = decimal
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    // comma = thousands sep, dot = decimal
    s = s.replace(/,/g, '');
  }

  const n = Number(s);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

export type DateFormat = 'DD-MM-YYYY' | 'YYYY-MM-DD' | 'DD/MM/YYYY' | 'MM/DD/YYYY';

export const DATE_FORMATS: DateFormat[] = ['YYYY-MM-DD', 'DD/MM/YYYY', 'DD-MM-YYYY', 'MM/DD/YYYY'];

/** Strip any trailing time component, keeping just the leading date token:
 *  "2026-05-01 03:58:02" → "2026-05-01"; "02/06/2026, 15:05" → "02/06/2026";
 *  "2026-05-01T03:58:02Z" → "2026-05-01". */
function dateToken(raw: string): string {
  return (raw ?? '').trim().split(/[\sT,]+/)[0] ?? '';
}

/**
 * Normalize a date string in one of the supported input formats to the
 * canonical ISO `YYYY-MM-DD` the API expects. Returns `null` on failure.
 * A trailing time component (after a space, comma, or `T`) is ignored, so
 * bank exports like Revolut (`2026-05-01 03:58:02`) and Monzo
 * (`02/06/2026, 15:05`) parse. Accepts `-`, `/`, or `.` separators regardless
 * of the chosen format so minor bank variations still parse.
 */
export function parseDate(raw: string, format: DateFormat): string | null {
  if (!raw) return null;
  const parts = dateToken(raw).split(/[-/.]/).map((p) => p.trim());
  if (parts.length !== 3) return null;

  let year: string;
  let month: string;
  let day: string;
  if (format === 'YYYY-MM-DD') {
    [year, month, day] = parts as [string, string, string];
  } else if (format === 'MM/DD/YYYY') {
    [month, day, year] = parts as [string, string, string];
  } else {
    // DD-MM-YYYY and DD/MM/YYYY share day-month-year ordering.
    [day, month, year] = parts as [string, string, string];
  }

  if (year.length === 2) year = `20${year}`; // tolerate 2-digit years
  if (!/^\d{4}$/.test(year) || !/^\d{1,2}$/.test(month) || !/^\d{1,2}$/.test(day)) {
    return null;
  }
  const m = month.padStart(2, '0');
  const d = day.padStart(2, '0');
  if (Number(m) < 1 || Number(m) > 12 || Number(d) < 1 || Number(d) > 31) return null;
  return `${year}-${m}-${d}`;
}

/**
 * Infer the date format from a column's sample values. Returns the best guess,
 * or `null` if no value looks like a date. Year-first (4-digit leading) →
 * `YYYY-MM-DD`. Otherwise uses the separator and any component > 12 to decide
 * day-first vs month-first (defaulting to day-first, the pt/EU convention).
 */
export function detectDateFormat(samples: string[]): DateFormat | null {
  const tokens = samples.map(dateToken).filter((t) => /^\d{1,4}[-/.]\d{1,2}[-/.]\d{1,4}$/.test(t));
  if (tokens.length === 0) return null;

  const yearFirst = tokens.filter((t) => /^\d{4}[-/.]/.test(t)).length;
  if (yearFirst >= tokens.length / 2) return 'YYYY-MM-DD';

  const usesSlash = tokens.some((t) => t.includes('/'));
  let anyFirstGt12 = false;
  let anySecondGt12 = false;
  for (const t of tokens) {
    const [a, b] = t.split(/[-/.]/).map(Number);
    if (a !== undefined && a > 12) anyFirstGt12 = true;
    if (b !== undefined && b > 12) anySecondGt12 = true;
  }
  if (anySecondGt12 && !anyFirstGt12) return 'MM/DD/YYYY';
  return usesSlash ? 'DD/MM/YYYY' : 'DD-MM-YYYY';
}

/**
 * Infer the number format from a column's sample values. The decimal separator
 * is whichever of `,`/`.` appears LAST in a value; comma-decimal → 'pt'
 * (`1.234,56`), dot-decimal → 'en' (`1,234.56`). Values with no separator are
 * ignored. Defaults to 'en' when undecidable.
 */
export function detectNumberFormat(samples: string[]): NumberFormat {
  let pt = 0;
  let en = 0;
  for (const raw of samples) {
    const s = String(raw ?? '').replace(/[^\d.,]/g, '');
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');
    if (lastComma === -1 && lastDot === -1) continue;
    if (lastComma > lastDot) pt++;
    else en++;
  }
  return pt > en ? 'pt' : 'en';
}
